/**
 * Upload Service - Image upload to backend
 * Handles logo, screenshot, and avatar uploads
 */

import { httpClient } from '@/lib/http/fetchWrapper'
import { compressImage } from '@/lib/utils/compress-image'

// ============================================================================
// TYPES
// ============================================================================

export type ImageType = 'avatar' | 'logo' | 'screenshot'

export interface UploadResponse {
  key: string
  originalUrl: string
  thumbnailUrl: string
}

export interface UploadResult {
  success: true
  data: UploadResponse
}

export interface UploadError {
  success: false
  error: {
    message: string
    code?: string
  }
}

// ============================================================================
// COMPRESSION PRESETS
// ============================================================================

// NOTE: `quality` is informational only — compressImage always encodes lossless
// WebP (quality 1) and reads just maxWidth/maxHeight. Logos use a generous max
// dimension so they stay crisp at 2–3× DPI instead of being shrunk to a blur.
const COMPRESS_PRESETS: Record<ImageType, { maxWidth: number; maxHeight: number; quality: number }> = {
  screenshot: { maxWidth: 5000, maxHeight: 5000, quality: 0.99 },
  logo:       { maxWidth: 1024, maxHeight: 1024, quality: 1 },
  avatar:     { maxWidth: 256,  maxHeight: 256,  quality: 0.85 },
}

// ============================================================================
// UPLOAD SERVICE
// ============================================================================

/**
 * Upload an image file to the backend
 * Automatically compresses and converts to WebP before sending.
 *
 * @param file - The image file to upload
 * @param type - The type of image (avatar, logo, screenshot)
 * @returns Upload response with URLs or error
 */
export async function uploadImage(
  file: File,
  type: ImageType = 'screenshot'
): Promise<UploadResult | UploadError> {
  try {
    const compressed = await compressImage(file, COMPRESS_PRESETS[type])

    const formData = new FormData()
    formData.append('image', compressed)

    const response = await httpClient.postFormData<UploadResponse>(
      '/uploads',
      formData,
      { params: { type } }
    )

    return {
      success: true,
      data: response,
    }
  } catch (error) {
    console.error('Upload failed:', error)

    const message = error instanceof Error
      ? error.message
      : 'Failed to upload image'

    const code = (error as { code?: string })?.code

    return {
      success: false,
      error: { message, code },
    }
  }
}

/**
 * Upload logo image
 */
export async function uploadLogo(file: File): Promise<UploadResult | UploadError> {
  return uploadImage(file, 'logo')
}

/**
 * Upload screenshot image
 */
export async function uploadScreenshot(file: File): Promise<UploadResult | UploadError> {
  return uploadImage(file, 'screenshot')
}

/**
 * Upload avatar image
 */
export async function uploadAvatar(file: File): Promise<UploadResult | UploadError> {
  return uploadImage(file, 'avatar')
}
